// The organization's subscription (PLAN-offers.md §7, §8, §15). Every member sees which offer it is on; only
// owners and admins see its monthly credits, their use and the pack credits (the API withholds them). Owners and
// admins cancel and follow their orders here, and change the offer, buy credit packs or pay an order in the checkout
// (`/checkout`, CheckoutPage), which this page links to. Nothing takes effect before its order is paid; Mollie sends
// the admin back to `/billing?order={id}`. Renewals are charged on the saved card (M4), which admins
// replace (back on `/billing?card=1`) or remove. Offers and packs are shown in the organization's currency once it is
// fixed, else in the one guessed or chosen (components/currency.tsx), which the checkout links carry.
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { date, money } from '../components/format';
import { FREE_OFFER, PoolMeter, SubscriptionBadge, VatBreakdown, offerFacts, offerFeatures, offerPrice } from '../components/billing';
import { CardReturn, PayLink, PaymentReturn, SavedCardCard, payableByCard, savedCardText } from '../components/cardPayment';
import { CurrencySwitcher, shownCurrency } from '../components/currency';
import { Alert, Badge, Button, ButtonLink, Card, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { BillingState, CreditGrant, CreditPack, Offer, Order, Subscription } from '../types';

const CHANGE_ERRORS = ['seats_below_members', 'seats_out_of_range', 'no_change', 'trial_not_selectable', 'free_not_selectable', 'tier_required', 'tier_not_offered', 'packs_require_paid_offer', 'offer_not_found', 'pack_not_found', 'renewal_is_owed',
  'currency_mismatch', 'currency_locked', 'currency_not_offered'];

function errorText(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError && CHANGE_ERRORS.includes(err.code)) return t(`billing.error.${err.code}`);
  return t('app.error');
}

export function BillingPage() {
  const { t } = useTranslation();
  const { user, reload: reloadUser } = useSession();
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [grants, setGrants] = useState<CreditGrant[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [currency, setCurrency] = useState<{ shown: string; choices: string[]; org: string } | null>(null);
  const [error, setError] = useState(false);
  const admin = user?.organization?.role === 'owner' || user?.organization?.role === 'admin';
  const [params, setParams] = useSearchParams();
  // Back from Mollie: the order to check, kept once `?order=` is dropped from the URL.
  const [returning] = useState(() => Number(params.get('order')) || null);
  // Back from Mollie after saving a card (`?card=1`).
  const [returningCard] = useState(() => params.get('card') === '1');

  const load = useCallback(async () => {
    try {
      // A plain member is not shown the pack credits, so they are not asked for.
      const [b, org, g] = await Promise.all([api.subscription(), api.organization(), admin ? api.credits() : Promise.resolve([])]);
      const { currency: shown, choices } = await shownCurrency(org, params.get('currency'));
      const [o, p] = await Promise.all([api.offers(shown), api.creditPacks(shown)]);
      setBilling(b);
      setCurrency({ shown, choices, org: org.currency });
      setOffers(o.filter((x) => !x.trial && x.key !== FREE_OFFER));
      setPacks(p);
      setGrants(g);
      setOrders(admin ? await api.orders() : []);
    } catch {
      setError(true);
    }
    // `?currency=` is read when the page opens; the switcher takes over.
  }, [admin]);

  const switchCurrency = async (shown: string) => {
    try {
      const [o, p] = await Promise.all([api.offers(shown), api.creditPacks(shown)]);
      setOffers(o.filter((x) => !x.trial && x.key !== FREE_OFFER));
      setPacks(p);
      setCurrency((c) => c && { ...c, shown });
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  const changed = async () => {
    await load();
    await reloadUser();
  };

  const settled = (paid: boolean) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('order');
        next.delete('card');
        return next;
      },
      { replace: true },
    );
    if (paid) void changed();
  };

  if (error) return <Alert kind="error">{t('app.error')}</Alert>;
  if (!billing || !user || !currency) return <Spinner />;
  const sub = billing.subscription;

  return (
    <div className="flex flex-col gap-4">
      {returning !== null && admin && <PaymentReturn orderId={returning} onSettled={settled} />}
      {returningCard && admin && <CardReturn onSettled={settled} />}
      {billing.reason && <Alert kind={billing.reason === 'payment_overdue' ? 'error' : 'warn'}>{t(`billing.reason.${billing.reason}`)}</Alert>}
      {sub ? (
        <CurrentCard sub={sub} card={admin && billing.payments_enabled ? null : savedCardText(billing, t)} admin={admin} onChanged={changed} />
      ) : (
        <Card title={t('billing.title')}>{t('billing.noneHint')}</Card>
      )}
      {sub?.pending_order && <PendingOrder order={sub.pending_order} billing={billing} admin={admin} onChanged={changed} />}
      {admin && billing.payments_enabled && <SavedCardCard billing={billing} sub={sub} onChanged={changed} />}
      {admin ? <ChangeCard sub={sub} offers={offers} currency={currency.shown} choices={currency.choices} onCurrency={(c) => void switchCurrency(c)} /> : <Alert kind="info">{t('billing.adminsOnly')}</Alert>}
      {admin && <CreditsCard sub={sub} packs={packs} grants={grants} currency={sub?.currency ?? currency.org} admin={admin} />}
      {admin && orders.length > 0 && <OrdersCard orders={orders} billing={billing} />}
      {admin && sub && !sub.trial && Number(sub.monthly_total) > 0 && !sub.cancel_at_period_end && <CancelCard sub={sub} onChanged={changed} />}
    </div>
  );
}

function CurrentCard({ sub, card, admin, onChanged }: { sub: Subscription; card: string | null; admin: boolean; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const [error, setError] = useState<string | null>(null);
  const undo = async () => {
    setError(null);
    try {
      await api.undoScheduled();
      await onChanged();
    } catch (err) {
      setError(errorText(err, t));
    }
  };
  const end = date(sub.current_period_end, l);
  return (
    <Card title={t('billing.title')} actions={<SubscriptionBadge sub={sub} />}>
      <div className="flex flex-col gap-4">
        <dl className="grid gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2">
          <Fact label={t('billing.offer')} value={`${sub.offer_name}${sub.newer_version ? ` (${t('billing.version', { version: sub.version })})` : ''}`} />
          <Fact label={t('billing.price')} value={priceText(sub, t, l)} />
          {admin && sub.credit_tier_usage !== null && <Fact label={t('billing.tier')} value={t('billing.tierValue', { usage: money(sub.credit_tier_usage, sub.currency, l) })} />}
          <Fact label={t('billing.seats')} value={t('billing.seatsUsed', { used: sub.seats_used, seats: sub.seats })} />
          <Fact label={sub.trial ? t('billing.trialEnds') : sub.cancel_at_period_end ? t('billing.endsOn') : t('billing.renewsOn')} value={end} />
          {sub.user_cap_eur !== null && <Fact label={t('billing.userCap')} value={money(sub.user_cap_eur, sub.currency, l)} />}
          {admin && sub.credits !== null && <Fact label={t('billing.credits')} value={money(sub.credits, sub.currency, l)} />}
          {card && <Fact label={t('billing.card.saved')} value={card} />}
        </dl>
        {admin && (
          <PoolMeter
            spent={Number(sub.pool_spent ?? 0)}
            pool={sub.pool === null ? null : Number(sub.pool)}
            currency={sub.currency}
            locale={l}
            prorated={sub.pool_prorated_since && sub.pool_full !== null ? { since: sub.pool_prorated_since, full: Number(sub.pool_full), periodEnd: sub.current_period_end } : undefined}
          />
        )}
        {sub.newer_version && <p className="text-[12px] text-faint">{t('billing.grandfathered')}</p>}
        {sub.scheduled && (
          <Alert kind="warn">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {sub.scheduled.credit_tier_price !== null
                  ? t('billing.scheduledTier', { offer: sub.scheduled.offer_name, seats: sub.scheduled.seats, date: date(sub.scheduled.effective_at, l), tier: money(sub.scheduled.credit_tier_price, sub.currency, l) })
                  : t('billing.scheduled', { offer: sub.scheduled.offer_name, seats: sub.scheduled.seats, date: date(sub.scheduled.effective_at, l), price: money(sub.scheduled.price_per_seat, sub.currency, l) })}
              </span>
              {admin && (
                <Button variant="secondary" className="py-1! text-[12px]" onClick={() => void undo()}>
                  {t('billing.undo')}
                </Button>
              )}
            </div>
          </Alert>
        )}
        {sub.cancel_at_period_end && (
          <Alert kind="warn">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{t('billing.canceling', { date: end })}</span>
              {admin && (
                <Button variant="secondary" className="py-1! text-[12px]" onClick={() => void undo()}>
                  {t('billing.keep')}
                </Button>
              )}
            </div>
          </Alert>
        )}
        {error && <Alert kind="error">{error}</Alert>}
      </div>
    </Card>
  );
}

/** "€100 + €50 × 2 = €200 / month excl. VAT" when the price has several parts, "€30.00 / month excl. VAT" when it
 *  is the tier alone, or "Free". */
function priceText(sub: Subscription, t: (k: string, o?: Record<string, unknown>) => string, l: string): string {
  if (sub.trial || Number(sub.monthly_total) === 0) return t('billing.free');
  const total = money(sub.monthly_total, sub.currency, l);
  const parts: string[] = [];
  if (sub.credit_tier_price !== null) parts.push(t('billing.pricePartTier', { price: money(sub.credit_tier_price, sub.currency, l) }));
  if (Number(sub.price_per_seat) > 0 && (sub.seats > 1 || sub.credit_tier_price !== null))
    parts.push(t('billing.pricePartSeats', { price: money(sub.price_per_seat, sub.currency, l), count: sub.seats }));
  if (parts.length < 2) return t('billing.priceMonthly', { total });
  return t('billing.priceSum', { parts: parts.join(' + '), total });
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function PendingOrder({ order, billing, admin, onChanged }: { order: Order; billing: BillingState; admin: boolean; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  // Held until its VAT number is settled (PLAN-payments.md P9): nothing to pay yet, so no payment details.
  const held = order.vat_scheme === 'review';
  const instructions = billing.payment_instructions;
  const [error, setError] = useState<string | null>(null);
  const withdraw = async () => {
    if (!window.confirm(t('billing.withdrawConfirm'))) return;
    setError(null);
    try {
      await api.cancelOrder(order.id);
    } catch (err) {
      // A checkout is open on Mollie's page: the order waits for its outcome.
      setError(err instanceof ApiError && err.is('payment_in_progress') ? t('billing.card.error.payment_in_progress') : t('app.error'));
      return;
    }
    await onChanged();
  };
  return (
    <Alert kind={order.kind === 'renewal' ? 'warn' : 'info'}>
      <div className="flex flex-col gap-1.5" data-testid="pending-order">
        <strong className="font-medium text-ink">
          {t('billing.pendingTitle', { number: order.id, amount: money(order.amount, order.currency, i18n.language) })}
        </strong>
        <span>{order.lines.map((line) => line.description).join('; ')}</span>
        <VatBreakdown vat={order} currency={order.currency} admin={admin} />
        {!held && <span>{order.kind === 'renewal' ? t('billing.pendingRenewal') : t('billing.pendingHint')}</span>}
        {order.kind === 'renewal' && order.payment_status === 'failed' && (
          <strong className="font-medium text-danger" data-testid="renewal-charge-failed">
            {t('billing.card.autoFailed')}
          </strong>
        )}
        {instructions && !held && <span className="whitespace-pre-line font-mono text-[12px]">{instructions}</span>}
        {payableByCard(order, billing, admin) && (
          <div className="mt-1">
            <PayLink order={order} />
          </div>
        )}
        {error && <span className="text-[12px] text-danger">{error}</span>}
        {admin && order.kind !== 'renewal' && (
          <div>
            <Button variant="ghost" className="px-0! py-0.5! text-[12px] underline" onClick={() => void withdraw()}>
              {t('billing.withdraw')}
            </Button>
          </div>
        )}
      </div>
    </Alert>
  );
}

/** The offer cards fill the row whatever the number of offers on sale (Free is hidden from the choice). Static
 *  class names so Tailwind keeps them. */
const OFFER_COLUMNS = ['', '', 'sm:grid-cols-2', 'sm:grid-cols-3'];

function ChangeCard({ sub, offers, currency, choices, onCurrency }: { sub: Subscription | null; offers: Offer[]; currency: string; choices: string[]; onCurrency: (c: string) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const onPaid = sub !== null && !sub.trial && Number(sub.monthly_total) > 0;
  if (offers.length === 0 && choices.length === 0) return null;
  // The checkout opens in the currency shown here, while the admin may still choose it.
  const inCurrency = choices.length ? `currency=${currency}` : '';
  return (
    <Card
      title={onPaid ? t('billing.changeTitle') : t('billing.chooseTitle')}
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <CurrencySwitcher value={currency} choices={choices} onChange={onCurrency} />
          <ButtonLink to={inCurrency ? `/checkout?${inCurrency}` : '/checkout'} className="py-1! text-[12px]" data-testid="open-checkout">
            {onPaid ? t('billing.changeOffer') : t('billing.chooseOffer')}
          </ButtonLink>
        </div>
      }
    >
      <div className={`grid gap-2 ${OFFER_COLUMNS[Math.min(offers.length, 3)]}`}>
        {offers.map((o) => (
          <Link
            key={o.key}
            to={`/checkout?offer=${o.key}${inCurrency && `&${inCurrency}`}`}
            className="flex flex-col gap-1 rounded-md border border-line-soft p-3 text-[13px] hover:border-accent hover:bg-accent-bg"
            data-testid={`checkout-${o.key}`}
          >
            <span className="flex items-center gap-2">
              <span className="font-medium text-ink">{o.name}</span>
              {sub?.offer_key === o.key && <Badge tone="accent">{t('billing.current')}</Badge>}
            </span>
            <span className="flex flex-col text-ink" data-testid={`price-${o.key}`}>
              {offerPrice(o, t, l).map((x, i) => (
                <span key={x} className={i > 0 ? 'text-[12px] text-muted' : undefined}>
                  {x}
                </span>
              ))}
            </span>
            <ul className="flex flex-col gap-0.5 text-[12px] text-muted">
              {offerFacts(o, t, l).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            {offerFeatures(o, t).length > 0 && (
              <ul className="flex flex-col gap-0.5 text-[12px] text-faint">
                {offerFeatures(o, t).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
          </Link>
        ))}
      </div>
    </Card>
  );
}

/** `currency`: the organization's, which the credits are in. */
function CreditsCard({ sub, packs, grants, currency, admin }: { sub: Subscription | null; packs: CreditPack[]; grants: CreditGrant[]; currency: string; admin: boolean }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const now = Date.now();
  const live = grants.filter((g) => Number(g.remaining_eur) > 0 && (g.expires_at === null || new Date(g.expires_at).getTime() > now));
  return (
    <Card title={t('billing.creditsTitle')} actions={<span className="font-mono text-[13px] text-ink">{money(sub?.credits ?? 0, currency, l)}</span>}>
      <p className="text-[13px] text-muted">{t('billing.creditsHint')}</p>
      {live.length > 0 && (
        <ul className="mt-3 flex flex-col divide-y divide-line-row text-[13px]">
          {live.map((g) => (
            <li key={g.id} className="flex justify-between gap-3 py-1.5">
              <span className="text-muted">{g.source === 'staff' ? t('billing.grantStaff') : t('billing.grantPack')}</span>
              <span>
                <span className="font-mono text-ink">
                  {money(g.remaining_eur, g.currency, l)} / {money(g.amount_eur, g.currency, l)}
                </span>
                <span className="ml-2 text-muted">{g.expires_at ? t('billing.expiresOn', { date: date(g.expires_at, l) }) : t('pricing.noExpiry')}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {admin && packs.length > 0 && !(sub && Number(sub.monthly_total) > 0) && <p className="mt-3 text-[12.5px] text-faint">{t('billing.packsNeedPaid')}</p>}
      {admin && packs.length > 0 && sub && Number(sub.monthly_total) > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {packs.map((p) => (
            <div key={p.key} className="flex flex-col gap-2 rounded-md border border-line-soft p-3 text-[13px]">
              <span className="text-ink">{p.name}</span>
              <span className="font-mono text-[12px] text-muted">
                {money(p.price, p.currency, l)} · {p.validity_months ? t('pricing.validFor', { count: p.validity_months }) : t('pricing.noExpiry')}
              </span>
              <ButtonLink to={`/checkout?pack=${p.key}`} variant="secondary" className="py-1! text-center text-[12px]" data-testid={`buy-${p.key}`}>
                {t('billing.buy')}
              </ButtonLink>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function OrdersCard({ orders, billing }: { orders: Order[]; billing: BillingState }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  return (
    <Card title={t('billing.ordersTitle')}>
      <div className="-mx-2 overflow-x-auto">
        <table className="w-full min-w-[600px] text-[13px]">
          <thead>
            <tr className="text-left font-mono text-[10.5px] uppercase tracking-wider text-faint">
              <th className="px-2 py-1.5 font-normal">#</th>
              <th className="px-2 py-1.5 font-normal">{t('billing.orderWhat')}</th>
              <th className="px-2 py-1.5 font-normal">{t('billing.orderDate')}</th>
              <th className="px-2 py-1.5 text-right font-normal">{t('billing.amountExclVat')}</th>
              <th className="px-2 py-1.5 text-right font-normal">{t('billing.amountInclVat')}</th>
              <th className="px-2 py-1.5 font-normal">{t('invoices.status')}</th>
              {billing.payments_enabled && <th className="px-2 py-1.5 font-normal" />}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-line-row">
                <td className="px-2 py-2 font-mono text-[12px] text-muted">{o.id}</td>
                <td className="px-2 py-2">
                  <div className="text-ink">{t(`billing.kind.${o.kind}`)}</div>
                  <div className="text-[11.5px] text-faint">{o.lines.map((line) => line.description).join('; ')}</div>
                </td>
                <td className="px-2 py-2 text-muted">{date(o.created_at, l)}</td>
                <td className="px-2 py-2 text-right font-mono text-[12px] text-ink">{money(o.amount, o.currency, l)}</td>
                <td className="px-2 py-2 text-right font-mono text-[12px] text-ink">{o.total !== null ? money(o.total, o.currency, l) : o.vat_scheme === 'review' ? t('billing.vat.heldShort') : '—'}</td>
                <td className="px-2 py-2">
                  <Badge tone={o.status === 'paid' ? 'accent' : o.status === 'pending' ? 'warn' : 'neutral'}>{t(`billing.orderStatus.${o.status}`)}</Badge>
                </td>
                {billing.payments_enabled && <td className="px-2 py-2 text-right">{payableByCard(o, billing, true) && <PayLink order={o} compact />}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px] text-faint">
        {t('billing.invoicesHint')}{' '}
        <Link to="/invoices" className="text-accent underline">
          {t('nav.invoices')}
        </Link>
      </p>
    </Card>
  );
}

function CancelCard({ sub, onChanged }: { sub: Subscription; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const cancel = async () => {
    if (!window.confirm(t('billing.cancelConfirm', { date: date(sub.current_period_end, i18n.language) }))) return;
    setError(null);
    try {
      await api.cancelSubscription();
      await onChanged();
    } catch (err) {
      setError(errorText(err, t));
    }
  };
  return (
    <Card title={t('billing.cancelTitle')}>
      <p className="text-[13px] text-muted">{t('billing.cancelHint', { date: date(sub.current_period_end, i18n.language) })}</p>
      {error && (
        <div className="mt-3">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      <div className="mt-3">
        <Button variant="danger" onClick={() => void cancel()}>
          {t('billing.cancel')}
        </Button>
      </div>
    </Card>
  );
}
